/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import vegaEmbed, { VisualizationSpec } from "vega-embed";
import { IchartData } from "../shap-details.component";

/** Estimated width in pixels of one character of a bar label, in the chart's bold font */
const LABEL_CHAR_WIDTH = 7;
/** Space in pixels between a bar and its label placed next to it */
const LABEL_MARGIN = 4;
/** Vega expression of a bar's width in pixels */
const BAR_WIDTH = "abs(scale('x', datum.sum) - scale('x', datum.previous_sum))";
/** Vega expression of the estimated width in pixels of a bar's label (e.g. "16.89%"), from its number of characters */
const LABEL_WIDTH = `length(format(datum.value, '.2%')) * ${LABEL_CHAR_WIDTH}`;
/** Vega expression telling whether a bar is wide enough to hold its label */
const LABEL_FITS_IN_BAR = `${BAR_WIDTH} >= ${LABEL_WIDTH}`;
/** Vega expression telling whether there is room for a bar's label on its right, before the chart's edge */
const LABEL_FITS_ON_RIGHT = `scale('x', max(datum.sum, datum.previous_sum)) + ${LABEL_MARGIN} + ${LABEL_WIDTH} <= width`;

@Component({
  selector: "app-shap-chart",
  templateUrl: "./shap-chart.component.html",
  styleUrls: ["./shap-chart.component.scss"],
})
export class ShapChartComponent implements OnChanges {
  @Input() chartData!: IchartData[];
  public tooltipOptions!: { formatTooltip: (value: any, sanitize: any) => any };

  ROW_HEIGHT = 28;

  constructor(private translateService: TranslateService) {}

  public chartSpec: VisualizationSpec | any = {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    data: {
      values: [],
    },
    autosize: {
      type: "fit-x",
      contains: "padding",
    },
    width: "container",
    height: 450,
    transform: [
      { window: [{ op: "sum", field: "value", as: "sum" }] },
      { window: [{ op: "lead", field: "label", as: "lead" }] },
      {
        calculate: "datum.lead === null ? datum.label : datum.lead",
        as: "lead",
      },
      {
        calculate: "datum.id === 'output' ? 0 : datum.sum - datum.value",
        as: "previous_sum",
      },
      {
        calculate: "datum.id === 'output' ? datum.sum : datum.value",
        as: "value",
      },
      {
        calculate: "(datum.id !== 'baseValue' && datum.id !== 'output' && datum.value > 0 ? '+' : '') + datum.value",
        as: "text_value",
      },
      { calculate: "(datum.sum + datum.previous_sum) / 2", as: "center" },
      {
        calculate: "datum.sum < datum.previous_sum ? datum.sum : ''",
        as: "sum_dec",
      },
      {
        calculate: "datum.sum > datum.previous_sum ? datum.sum : ''",
        as: "sum_inc",
      },
    ],
    encoding: {
      y: {
        field: "label",
        type: "ordinal",
        sort: null,
        axis: { labelAngle: 0, title: "Features" },
      },
    },
    layer: [
      {
        mark: { type: "bar", size: this.ROW_HEIGHT, tooltip: { content: "data" } },
        encoding: {
          x: {
            field: "previous_sum",
            type: "quantitative",
            title: "",
            sort: "ascending",
            axis: { labels: false },
          },
          x2: { field: "sum" },
          color: {
            condition: [
              {
                test: "datum.id === 'baseValue' || datum.id === 'output'",
                value: "#86afcf",
              },
              { test: "datum.sum < datum.previous_sum", value: "#E54E35" },
            ],
            value: "#3EAA5A",
          },
        },
      },
      {
        mark: {
          type: "text",
          fontWeight: "bold",
          baseline: "middle",
          angle: 0,
          tooltip: { content: "data" },
          // Centered in its bar when it fits, otherwise next to it: on its right, or on its left at the chart's edge
          align: { expr: `${LABEL_FITS_IN_BAR} ? 'center' : ${LABEL_FITS_ON_RIGHT} ? 'left' : 'right'` },
          dx: {
            expr: `${LABEL_FITS_IN_BAR} ? 0 : (${LABEL_FITS_ON_RIGHT} ? 1 : -1) * (${BAR_WIDTH} / 2 + ${LABEL_MARGIN})`,
          },
        },
        encoding: {
          x: { field: "center", type: "quantitative" },
          text: { field: "text_value", type: "nominal", format: ".2%" },
          color: {
            condition: [{ test: LABEL_FITS_IN_BAR, value: "white" }],
            value: "#404040",
          },
        },
      },
    ],
    config: { text: { fontWeight: "bold", color: "#404040", angle: 90 } },
  };

  /**
   * Recomputes the chart height, tooltip formatter and data, then re-renders the Vega chart.
   * @param _changes Angular input changes
   */
  ngOnChanges(_changes: SimpleChanges): void {
    const MIN_HEIGHT = 200;

    this.chartSpec.encoding.y.axis.title = this.translateService.instant("metadataPage.prediction.shap.features");
    this.chartSpec.height = Math.max(this.chartData.length * this.ROW_HEIGHT, MIN_HEIGHT);
    this.tooltipOptions = {
      formatTooltip: (value, sanitize) => {
        switch (value.id) {
          case "baseValue":
            return this.translateService.instant("metadataPage.prediction.shap.tooltip.baseValue");
          case "output":
            return this.translateService.instant("metadataPage.prediction.shap.tooltip.output");
          default:
            return this.translateService.instant("metadataPage.prediction.shap.tooltip.text", {
              parameter: sanitize(value.parameter),
              assessment: this.computeAssessment(sanitize(value.value)),
              effect: this.computeEffect(sanitize(value.value)),
              value: Math.round(sanitize(value.value) * 100 * 100) / 100,
            });
        }
      },
    };

    this.chartSpec.data.values = this.chartData;
    vegaEmbed("#vis", this.chartSpec, { tooltip: this.tooltipOptions });
  }

  /**
   * Translates the sign of a SHAP value into a positive/negative assessment label.
   * @param value SHAP value to assess
   * @return Translated label describing whether the value is positive, negative, or neutral
   */
  private computeAssessment(value: number): string {
    if (Math.sign(value) === 1) {
      return this.translateService.instant("metadataPage.prediction.shap.tooltip.positive");
    } else if (Math.sign(value) === -1) {
      return this.translateService.instant("metadataPage.prediction.shap.tooltip.negative");
    }
    return "";
  }

  /**
   * Translates the sign of a SHAP value into an increase/decrease effect label.
   * @param value SHAP value to assess
   * @return Translated label describing whether the value increases, decreases, or has no effect
   */
  private computeEffect(value: number): string {
    if (Math.sign(value) === -1) {
      return this.translateService.instant("metadataPage.prediction.shap.tooltip.decrease");
    } else if (Math.sign(value) === 1) {
      return this.translateService.instant("metadataPage.prediction.shap.tooltip.increase");
    }
    return "";
  }
}
